import { Switch } from '@listening/frontend/components/radix/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@listening/frontend/components/radix/select';
import { cn } from '@listening/utils/react-helpers';
import { menuSurface } from '@/components/web-fe/design-v2/surfaces';
import {
  type DocActiveEmphasisKey,
  type DocFontKey,
  docActiveEmphasisOptions,
  docContentWidthOptions,
  docFontOptions,
  docFontWeightOptions,
  docLineHeightOptions,
  docParagraphSpacingOptions,
  docTextSizeOptions,
  useAudioPrefsStore,
} from '@listening/frontend/stores/audio-preferences-store';
import { NavArrowLeft, NavArrowRight } from 'iconoir-react';

function useTextSettings() {
  return {
    docFont: useAudioPrefsStore((s) => s.docFont),
    setDocFont: useAudioPrefsStore((s) => s.setDocFont),
    docFontWeight: useAudioPrefsStore((s) => s.docFontWeight),
    setDocFontWeight: useAudioPrefsStore((s) => s.setDocFontWeight),
    docTextSize: useAudioPrefsStore((s) => s.docTextSize),
    setDocTextSize: useAudioPrefsStore((s) => s.setDocTextSize),
    docLineHeight: useAudioPrefsStore((s) => s.docLineHeight),
    setDocLineHeight: useAudioPrefsStore((s) => s.setDocLineHeight),
    docContentWidth: useAudioPrefsStore((s) => s.docContentWidth),
    setDocContentWidth: useAudioPrefsStore((s) => s.setDocContentWidth),
    docParagraphSpacing: useAudioPrefsStore((s) => s.docParagraphSpacing),
    setDocParagraphSpacing: useAudioPrefsStore((s) => s.setDocParagraphSpacing),
    docActiveEmphasis: useAudioPrefsStore((s) => s.docActiveEmphasis),
    setDocActiveEmphasis: useAudioPrefsStore((s) => s.setDocActiveEmphasis),
  };
}

function Section({
  label,
  first,
  children,
}: {
  label: string;
  first?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'px-[18px] py-3.5',
        !first && 'border-t border-t-[var(--v2-card-border)]',
      )}
    >
      <div className='mb-2.5 text-[10px] font-semibold tracking-[0.14em] text-[var(--v2-text-muted)] uppercase'>
        {label}
      </div>
      <div className='flex flex-col gap-1'>{children}</div>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className='grid grid-cols-[108px_1fr] items-center gap-3 py-1.5'>
      <div className='text-[13px] font-medium text-[var(--v2-text-primary)]'>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Stepper<T extends string>({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<{ key: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  const idx = options.findIndex((o) => o.key === value);
  const safeIdx = idx === -1 ? 0 : idx;
  const current = options[safeIdx];
  const canDec = safeIdx > 0;
  const canInc = safeIdx < options.length - 1;
  return (
    <div className='flex h-8 items-center justify-between rounded-[var(--v2-button-radius)] bg-[var(--v2-card-bg)] px-1'>
      <button
        type='button'
        disabled={!canDec}
        onClick={() => canDec && onChange(options[safeIdx - 1].key)}
        className={cn(
          'flex size-6 items-center justify-center rounded-md',
          canDec
            ? 'text-[var(--v2-accent)] hover:bg-black/[0.05] dark:hover:bg-white/10'
            : 'text-[var(--v2-text-muted)] opacity-40',
        )}
      >
        <NavArrowLeft className='size-4' strokeWidth={2.25} />
      </button>
      <span className='text-[13px] font-medium text-[var(--v2-text-primary)]'>
        {current?.label ?? ''}
      </span>
      <button
        type='button'
        disabled={!canInc}
        onClick={() => canInc && onChange(options[safeIdx + 1].key)}
        className={cn(
          'flex size-6 items-center justify-center rounded-md',
          canInc
            ? 'text-[var(--v2-accent)] hover:bg-black/[0.05] dark:hover:bg-white/10'
            : 'text-[var(--v2-text-muted)] opacity-40',
        )}
      >
        <NavArrowRight className='size-4' strokeWidth={2.25} />
      </button>
    </div>
  );
}

function ActiveStyles({
  value,
  onChange,
}: {
  value: DocActiveEmphasisKey;
  onChange: (v: DocActiveEmphasisKey) => void;
}) {
  return (
    <div className='flex gap-1.5'>
      {docActiveEmphasisOptions.map((opt) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type='button'
            onClick={() => onChange(opt.key)}
            className={cn(
              'flex h-8 flex-1 items-center justify-center rounded-[var(--v2-button-radius)] border text-[13px] transition-all',
              active
                ? 'border-[var(--v2-outline-strong)] bg-[var(--v2-page-bg)] shadow-[0_1px_2px_rgb(0_0_0/0.08)]'
                : 'border-transparent bg-[var(--v2-card-bg)]',
            )}
          >
            {opt.key === 'color' && (
              <span className='text-[var(--v2-accent)]'>word</span>
            )}
            {opt.key === 'highlight' && (
              <span className='rounded bg-[var(--v2-accent)]/15 px-1.5 py-0.5 text-[var(--v2-accent)]'>
                word
              </span>
            )}
            {opt.key === 'underline' && (
              <span className='text-[var(--v2-text-primary)] underline decoration-[var(--v2-accent)] decoration-2 underline-offset-[3px]'>
                word
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function FontFamilySelect({
  value,
  onChange,
}: {
  value: DocFontKey;
  onChange: (v: DocFontKey) => void;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as DocFontKey)}>
      <SelectTrigger className='h-8 rounded-[var(--v2-button-radius)] border-transparent bg-[var(--v2-card-bg)] px-2.5 text-center text-[13px] font-medium text-[var(--v2-text-primary)] shadow-none focus:ring-[var(--v2-accent)] focus:ring-offset-0 [&>span]:flex-1'>
        <SelectValue />
      </SelectTrigger>
      <SelectContent position='item-aligned' className={menuSurface}>
        {docFontOptions.map((opt) => (
          <SelectItem
            key={opt.key}
            value={opt.key}
            className={cn('text-[13px]', opt.className)}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function TextViewSettingsPanel({
  className,
  mode = 'text',
}: {
  className?: string;
  mode?: 'pdf' | 'text';
}) {
  const s = useTextSettings();
  const autoScroll = useAudioPrefsStore((st) => st.autoScroll);
  const setAutoScroll = useAudioPrefsStore((st) => st.setAutoScroll);
  const showTextSettings = mode === 'text';

  return (
    <div className={cn('flex flex-col', className)}>
      {showTextSettings && (
        <>
          <Section label='Text' first>
            <Row label='Family'>
              <FontFamilySelect
                value={s.docFont}
                onChange={(v) => s.setDocFont(v)}
              />
            </Row>
            <Row label='Weight'>
              <Stepper
                options={docFontWeightOptions}
                value={s.docFontWeight}
                onChange={(v) => s.setDocFontWeight(v)}
              />
            </Row>
            <Row label='Size'>
              <Stepper
                options={docTextSizeOptions}
                value={s.docTextSize}
                onChange={(v) => s.setDocTextSize(v)}
              />
            </Row>
          </Section>

          <Section label='Layout'>
            <Row label='Width'>
              <Stepper
                options={docContentWidthOptions}
                value={s.docContentWidth}
                onChange={(v) => s.setDocContentWidth(v)}
              />
            </Row>
            <Row label='Line spacing'>
              <Stepper
                options={docLineHeightOptions}
                value={s.docLineHeight}
                onChange={(v) => s.setDocLineHeight(v)}
              />
            </Row>
            <Row label='Paragraph spacing'>
              <Stepper
                options={docParagraphSpacingOptions}
                value={s.docParagraphSpacing}
                onChange={(v) => s.setDocParagraphSpacing(v)}
              />
            </Row>
          </Section>
        </>
      )}

      <Section label='Reading' first={!showTextSettings}>
        {showTextSettings && (
          <Row label='Active'>
            <ActiveStyles
              value={s.docActiveEmphasis}
              onChange={(v) => s.setDocActiveEmphasis(v)}
            />
          </Row>
        )}
        <Row label='Auto scroll'>
          <div className='flex justify-end'>
            <Switch checked={autoScroll} onCheckedChange={setAutoScroll} />
          </div>
        </Row>
      </Section>
    </div>
  );
}
